import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Loader2, Send, ShieldCheck } from "lucide-react";
import { submitContact } from "@/lib/api";

const LOAN_TYPES = [
    "Home Loan", "First Home Buyer", "Refinance", "Investment Loan",
    "Commercial Loan", "Self-Employed Loan", "Car / Asset Finance", "Personal Loan", "Other"
];

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export const ContactForm = () => {
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", loan_type: "", message: "" });
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const turnstileRef = useRef(null);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const resetTurnstile = () => {
        setToken("");
        try { turnstileRef.current?.reset?.(); } catch { /* noop */ }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
            toast.error("Please complete name, email and phone.");
            return;
        }
        if (TURNSTILE_SITE_KEY && !token) {
            toast.error("Please wait for the security check to complete.");
            return;
        }
        setLoading(true);
        try {
            await submitContact({
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                loan_type: form.loan_type || null,
                message: form.message.trim() || null,
                turnstile_token: token || null
            });
            setDone(true);
            toast.success("Thanks — Gurpreet will be in touch shortly.");
            setForm({ full_name: "", email: "", phone: "", loan_type: "", message: "" });
        } catch (err) {
            let msg = "Could not send your enquiry. Please try again.";
            const detail = err?.response?.data?.detail;
            if (typeof detail === "string") {
                msg = detail;
            } else if (Array.isArray(detail) && detail.length) {
                msg = detail.map((d) => d?.msg || "").filter(Boolean).join(" · ") || msg;
            } else if (err?.message && !err?.response) {
                msg = `Network error: ${err.message}`;
            }
            toast.error(msg);
            resetTurnstile();
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid="contact-form-success" className="bg-[#F3F1EA] border border-border rounded-sm p-10 text-center">
                <div className="font-serif text-3xl text-[#1A362B]">Thank you.</div>
                <div className="divider-thin mx-auto my-5" />
                <p className="text-[#1C1C1C]/75 max-w-md mx-auto">
                    Your enquiry has been received. Gurpreet will personally reach out within one business day.
                </p>
                <button
                    onClick={() => { setDone(false); resetTurnstile(); }}
                    data-testid="contact-form-new"
                    className="btn-outline mt-8"
                >
                    Send another enquiry
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-border rounded-sm p-8 md:p-10 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <Label htmlFor="cf-name" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Full name</Label>
                    <Input
                        id="cf-name"
                        data-testid="contact-input-name"
                        value={form.full_name}
                        onChange={update("full_name")}
                        placeholder="Jane Smith"
                        className="mt-2 h-12 border-border"
                        required
                    />
                </div>
                <div>
                    <Label htmlFor="cf-email" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Email</Label>
                    <Input
                        id="cf-email"
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="jane@email.com"
                        className="mt-2 h-12 border-border"
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <Label htmlFor="cf-phone" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Phone</Label>
                    <Input
                        id="cf-phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="04xx xxx xxx"
                        className="mt-2 h-12 border-border"
                        required
                    />
                </div>
                <div>
                    <Label className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">I'm interested in</Label>
                    <Select value={form.loan_type} onValueChange={(v) => setForm((f) => ({ ...f, loan_type: v }))}>
                        <SelectTrigger data-testid="contact-input-loantype" className="mt-2 h-12 border-border">
                            <SelectValue placeholder="Select a loan type" />
                        </SelectTrigger>
                        <SelectContent>
                            {LOAN_TYPES.map((t) => (
                                <SelectItem key={t} value={t} data-testid={`contact-loantype-${t}`}>{t}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
                <Label htmlFor="cf-message" className="text-xs tracking-widest uppercase font-semibold text-[#1A362B]">Tell us a bit more</Label>
                <Textarea
                    id="cf-message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Your situation, goals or any questions..."
                    rows={5}
                    className="mt-2 border-border resize-none"
                />
            </div>

            {TURNSTILE_SITE_KEY && (
                <div data-testid="contact-turnstile" className="flex items-center gap-3 flex-wrap">
                    <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        onSuccess={(t) => setToken(t)}
                        onError={() => setToken("")}
                        onExpire={() => setToken("")}
                        options={{ theme: "light", appearance: "always" }}
                    />
                    {token && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#1A362B]/70">
                            <ShieldCheck className="w-3.5 h-3.5 text-[#1A362B]" /> Verified
                        </span>
                    )}
                </div>
            )}

            <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading || (TURNSTILE_SITE_KEY && !token)}
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:pointer-events-none"
            >
                {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                    <>Send enquiry <Send className="w-4 h-4" /></>
                )}
            </button>

            <p className="text-xs text-[#1C1C1C]/50 leading-relaxed">
                By submitting you consent to be contacted by Finance Wealth regarding your enquiry. We do not share your information.
            </p>
            <p data-testid="contact-form-disclaimer" className="text-xs text-[#1C1C1C]/55 leading-relaxed italic border-l-2 border-[#C69C6D] pl-3 mt-2">
                Disclaimer: Your complete financial situation will need to be assessed before acceptance of any proposal or product.
            </p>
        </form>
    );
};

export default ContactForm;
